import { iconDadosUsuario, iconNavLista, iconNavNotificacao } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BrandHorizontalRed from '../../assets/BrandHorizontalRed.png';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyStore } from '../../store/companyStore';
import type { NavbarProps } from '../../types/componentType';
import { SystemMessageConfirmation } from '../index';

import CompanyCard from "./CompanyCard";

function Navbar({ onMenuToggle, isLoggingOut = false }: NavbarProps) {
  const { user, logout } = useAuth();
  const { selectedCompany } = useCompanyStore();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const userName = user?.pessoaFisicaResponse?.pessoaFisica?.nome;

  const navId = useId();
  const userMenuButtonId = useId();
  const userMenuId = useId();
  const overlayId = useId();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
  };

  const handleConfirmLogout = async () => {
    await logout();
    setShowLogoutModal(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleShowProfile = () => {
    navigate('/profile');
    setIsUserMenuOpen(false);
  };

  return (
    <nav
      id={navId}
      className="fixed top-0 left-0 right-0 z-50 shadow-md md:shadow-none"
      role="navigation"
      aria-label="Navegação principal"
    >
      {/* Navbar with gradient */}
      <div className="bg-background-secondary">
        <div className="w-full bsds-pl-24 bsds-pr-24 bsds-pt-16 bsds-pb-16">
          <div className="flex items-center justify-between w-full">

            {/* Left side - Mobile menu + Logo */}
            <div className="flex items-center">
              {/* Mobile menu */}
              <div className="flex items-center lg:hidden mr-4">
                <button
                  onClick={onMenuToggle}
                  className="text-white hover:text-gray-200 focus:outline-none focus:text-gray-200 transition-colors duration-200"
                  aria-label="Abrir menu de navegação"
                  aria-expanded="false"
                  aria-controls="sidebar-menu"
                >
                  <BsdsIcon icon={iconNavLista} className="w-6 h-12 text-primary-500" aria-hidden="true" />
                </button>
              </div>

              {/* Logo/Brand */}
              <div className="shrink-0 lg:block hidden">
                <div className="flex items-center">
                  <img
                    src={BrandHorizontalRed}
                    alt="Bradesco Seguros - Logo"
                    className="h-8"
                  />
                </div>
              </div>
            </div>

            {/* Right side - Desktop menu */}
            <div className="hidden lg:flex items-center space-x-3 gap-4">
              <CompanyCard
                company={selectedCompany}
              />

              {/* Bell icon */}
              <button
                className=""
                aria-label="Ver notificações"
                aria-haspopup="true"
              >
                <BsdsIcon icon={iconNavNotificacao} className="w-6 h-6 text-primary-500 mt-1.5" aria-hidden="true" />
              </button>

              {/* User icon */}
              <div className="relative">
                <button
                  id={userMenuButtonId}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center justify-center w-8 h-8 bg-primary-500 bg-opacity-20 rounded-full hover:bg-opacity-30 focus:outline-none focus:bg-opacity-30 transition-all duration-200"
                  aria-label={`Menu do usuário${userName ? `: ${userName}` : ''}`}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  aria-controls={userMenuId}
                >
                  <BsdsIcon icon={iconDadosUsuario} className="w-4 h-4 text-white" aria-hidden="true" />
                </button>

                {/* User dropdown */}
                {isUserMenuOpen && (
                  <div
                    id={userMenuId}
                    role="menu"
                    aria-labelledby={userMenuButtonId}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-custom-border py-1 z-50"
                    aria-label="Menu de opções do usuário"
                  >
                    <div className="px-4 py-2 text-sm text-gray-700 border-b" role="none">
                      <p className="font-medium" aria-label={`Usuário: ${userName}`}>
                        {userName}
                      </p>
                    </div>
                    <button
                      onClick={handleShowProfile}
                      role="menuitem"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                      aria-label="Abrir página de perfil"
                    >
                      Perfil
                    </button>
                    <button
                      onClick={handleLogoutClick}
                      role="menuitem"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                      aria-label="Sair do sistema"
                    >
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right side - Mobile menu */}
            <div className="lg:hidden flex items-center space-x-3">
              <CompanyCard
                company={selectedCompany}
              />


              {/* Bell icon */}
              <button
                className="mt-2"
                aria-label="Ver notificações"
                aria-haspopup="true"
              >
                <BsdsIcon icon={iconNavNotificacao} className="w-6 h-6 text-primary-500" aria-hidden="true" />
              </button>

              {/* User icon */}
              <div className="relative">
                <button
                  id={`${userMenuButtonId}-mobile`}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center justify-center w-8 h-8 bg-primary-500 bg-opacity-20 rounded-full hover:bg-opacity-30 focus:outline-none focus:bg-opacity-30 transition-all duration-200"
                  aria-label={`Menu do usuário${userName ? `: ${userName}` : ''}`}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  aria-controls={`${userMenuId}-mobile`}
                >
                  <BsdsIcon icon={iconDadosUsuario} className="w-4 h-4 text-white" aria-hidden="true" />
                </button>

                {/* User dropdown */}
                {isUserMenuOpen && (
                  <div
                    id={`${userMenuId}-mobile`}
                    role="menu"
                    aria-labelledby={`${userMenuButtonId}-mobile`}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                    aria-label="Menu de opções do usuário"
                  >
                    <div className="px-4 py-2 text-sm text-gray-700 border-b" role="none">
                      <p className="font-medium" aria-label={`Usuário: ${userName}`}>
                        {userName}
                      </p>
                    </div>
                    <button
                      onClick={handleShowProfile}
                      role="menuitem"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                      aria-label="Abrir página de perfil"
                    >
                      Perfil
                    </button>
                    <button
                      onClick={handleLogoutClick}
                      role="menuitem"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                      aria-label="Sair do sistema"
                    >
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay to close menu when clicking outside */}
      {isUserMenuOpen && (
        <div
          id={overlayId}
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsUserMenuOpen(false);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu de usuário"
          aria-hidden={isUserMenuOpen ? 'false' : 'true'}
        />
      )}

      {/* Modal confirmation logout */}
      <SystemMessageConfirmation
        type="confirmation"
        isOpen={showLogoutModal}
        isLoading={isLoggingOut}
        title="Confirmar Logout"
        message="Tem certeza que deseja sair do sistema?"
        confirmText="Sair"
        cancelText="Cancelar"
        confirmButtonVariant="danger"
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />

    </nav>
  );
}

export default Navbar;
